import React, { useContext } from "react";
import styled, { css } from "styled-components/macro";
import { DefaultFont } from "../../styles/fonts";

type TabValue = null | string | number;

interface ITab {
  children: React.ReactNode;
  value: TabValue;
}
interface ITabs {
  value: TabValue;
  onChange: (value: TabValue) => void;
  children: React.ReactNode;
  tabsCount: number;
}

interface ITabContext {
  value: TabValue;
  onChange: (value: TabValue) => void;
}

const TabContext = React.createContext<ITabContext>({
  value: null,
  onChange: () => undefined
});

const TabRoot = styled.div<{ isSelected: boolean }>`
  ${DefaultFont};
  color: #333333;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 14px !important;
  }

  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  ${(p) =>
    p.isSelected
      ? css`
          text-shadow: 0.02em 0 black, -0.02em 0 black, 0 0.02em black,
            0 -0.02em black;
        `
      : ``}
`;

const TabsRoot = styled.div<{ $tabsCount: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$tabsCount}, auto);
  column-gap: 5px;

  ${TabRoot} {
    &:after {
      content: "";
      height: 100%;
      width: 1px;
      border-right: 1px solid ${(p) => p.theme.colors.chatTextColor};
      margin-left: 10px;
      margin-right: 10px;
    }
    &:last-child:after {
      display: none;
    }
  }
`;

export const Tabs: React.FC<ITabs> & {
  Tab: React.FC<ITab>;
} = ({ onChange, value, children, tabsCount }) => {
  return (
    <TabsRoot $tabsCount={tabsCount}>
      <TabContext.Provider
        value={{
          value,
          onChange
        }}
      >
        {children}
      </TabContext.Provider>
    </TabsRoot>
  );
};

const Tab: React.FC<ITab> = ({ children, value }) => {
  const { value: currentValue, onChange } = useContext(TabContext);

  return (
    <TabRoot
      isSelected={value === currentValue}
      onClick={() => onChange(value)}
    >
      {children}
    </TabRoot>
  );
};

Tabs.Tab = Tab;
